import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import DOMPurify from 'dompurify';
import { get, debounce } from 'lodash';
import {
  AbsoluteBox,
  Box,
  Checkbox,
  CircularProgress,
  FlexBox,
  Grid,
  Icon,
  InputField,
  MuiBox,
  Typography,
  color,
  textSize
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { Keyboard } from 'components/Keyboard';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { NumPadAction } from 'components/NumPad';
import { PhoneNumberInput, Country } from 'components/PhoneNumberInput';
import { Scrollbar } from 'components/Scrollbar';
import { SelectDropdown } from 'components/SelectDropdown';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, actions as cartActions, Cart, Item } from 'services/cart';
import {
  selectors as configSelectors,
  ConfigInputField,
  LanguageField,
  LocalizedStrRecord,
  MembershipSalesSettings
} from 'services/configuration';
import { actions as customerActions, selectors as customerSelectors } from 'services/customer';
import {
  actions as ticketingActions,
  selectors as ticketingSelectors,
  MembershipType,
  MembershipField,
  UpsellStore
} from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { validateInput, validateInputType } from 'utils/validation';
import { useStyles } from './styled';

const MembershipDataCollectionView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currencyConfig = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  let displayFieldIdx = -1;

  const {
    reserveMembership,
    releaseCart,
    removeTicketsFromCart,
    resetCartError,
    setPhoneCountry,
    getLocations,
    resetLocations,
    resetCustomerError,
    setUpsell
  } = useActions({
    reserveMembership: cartActions.reserveMembership,
    releaseCart: cartActions.releaseCart,
    removeTicketsFromCart: cartActions.removeTicketsFromCart,
    resetCartError: cartActions.resetCartError,
    setPhoneCountry: customerActions.setPhoneCountry,
    getLocations: customerActions.getLocations,
    resetLocations: customerActions.resetLocations,
    resetCustomerError: customerActions.resetError,
    setUpsell: ticketingActions.setUpsell
  });

  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const cartLoading = useSelector(cartSelectors.selectLoading);
  const cartError = useSelector(cartSelectors.selectError);
  const configuration = useSelector(configSelectors.selectConfiguration);
  const customer = useSelector(customerSelectors.selectCustomer);
  const locations = useSelector(customerSelectors.selectLocations);
  const phoneCountry = useSelector(customerSelectors.selectPhoneCountry);
  const customerError = useSelector(customerSelectors.selectError);
  const membershipProduct = useSelector(ticketingSelectors.selectMembershipProduct);
  const { selected: selectedLevel } = useSelector(ticketingSelectors.selectMembershipProductConfig);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const upsell = useSelector(ticketingSelectors.selectUpsell) as UpsellStore;
  const upsellProducts = useSelector(ticketingSelectors.selectUpsellProducts);

  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary', '');
  const primaryPalette = get(configuration, 'configuration.globalSettings.palette.primary');
  const emailSuggestions: string[] = get(configuration, `messaging.${i18n.language}.emailSuggestions.buttons`, [
    '@gmail.com',
    '@yahoo.com',
    '@outlook.com'
  ]);
  const membershipSalesSettingsList: MembershipSalesSettings[] = get(
    configuration,
    'ticketing.membershipSalesSettingsList',
    []
  );
  const support = get(configuration, `${ticketingPath}.supportName.${i18n.language}`, i18n.t('commonText.support'));
  const priorityCountries: string[] = get(configuration, `${ticketingPath}.priorityPostalCodeLookupCountries`, []);

  const [uiLoading, setUILoading] = React.useState(true);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [isReservationPending, setIsReservationPending] = React.useState(false);
  const [membershipProductSettings, setMembershipProductSettings] = React.useState<any>(null);
  const [membershipProductLevel, setMembershipProductLevel] = React.useState<any>(null);
  const [membershipProductTypes, setMembershipProductTypes] = React.useState<MembershipType[]>([]);
  const [apiKey, setAPIKey] = React.useState('');
  const [showConfirmationOverlay, setShowConfirmationOverlay] = React.useState(false);
  const [displayFields, setDisplayFields] = React.useState<MembershipField[]>([]);
  const [error, setError] = React.useState<Record<string, string>>({});
  const [formData, setFormData] = React.useState<Record<string, string>>({});
  const formRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [hideScrollBar, setHideScrollBar] = React.useState(false);
  const [inputControl, setInputControl] = React.useState('');
  const [inputIndex, setInputIndex] = React.useState(-1);
  const [selectedField, setSelectedField] = React.useState<MembershipField | null>(null);
  const [selectedTypeIndexes, setSelectedTypeIndexes] = React.useState<number[]>([]);
  const [prefilledFieldIndexes, setPrefilledFieldIndexes] = React.useState<number[]>([]);
  const [validation, setValidation] = React.useState(false);
  const [validationComplete, setValidationComplete] = React.useState(false);
  const [phoneNumberInput, setPhoneNumberInput] = React.useState<{ keyboard: string; selectedCountry?: Country }>({
    keyboard: '',
    selectedCountry: phoneCountry
  });
  const [countryOptions, setCountryOptions] = React.useState<string[]>([]);
  const [stateOptions, setStateOptions] = React.useState<string[]>([]);
  const [cityOptions, setCityOptions] = React.useState<string[]>([]);
  const [selectedCountryOption, setSelectedCountryOption] = React.useState<string>('');
  const [selectedStateOption, setSelectedStateOption] = React.useState<string>('');
  const [selectedCityOption, setSelectedCityOption] = React.useState<string>('');
  const [locationFieldsDisabled, setLocationFieldsDisabled] = React.useState<boolean>(true);

  const classes = useStyles({ fontFamily, inputControl })();

  // Reset data when the component mounts or re-renders
  useEffect(() => {
    resetLocations();
  }, []);

  // Get the related config and the level that was chosen by the user
  useEffect(() => {
    if (membershipProduct) {
      setMembershipProductSettings(membershipSalesSettingsList.find((mss) => mss.name === membershipProduct.name));
      if (selectedLevel && membershipProduct.levels) {
        setMembershipProductLevel(membershipProduct.levels.find((level) => level.name === selectedLevel));
      } else {
        onForwardNav();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipProduct, selectedLevel]);

  // Set membershipProductTypes and displayFields when membershipProductLevel is set
  useEffect(() => {
    if (membershipProductLevel) {
      const membershipTypes: MembershipType[] = get(membershipProductLevel, 'types', []);
      setMembershipProductTypes(membershipTypes);

      const tempFields: MembershipField[] = [];
      membershipTypes.forEach((mt: MembershipType, mtIdx: number) => {
        // For the 1st type only: check for configured fields
        if (mtIdx === 0) {
          const backendFields: MembershipField[] = get(mt, 'fields', []);
          const configuredFields: MembershipField[] = get(membershipProductSettings, 'dataCollection.fields', []);
          // Merge the configured fields with the fields provided from the BE
          backendFields.forEach((backendField) => {
            const configuredFieldIdx = configuredFields.findIndex((cf) => cf.apiParam === backendField.apiParam);
            if (configuredFieldIdx > -1) {
              // If there are any shared props, like 'required': the FE config will override the BE
              configuredFields[configuredFieldIdx] = {
                ...backendField,
                ...configuredFields[configuredFieldIdx]
              };
            } else {
              configuredFields.push(backendField);
            }
          });
          // The FE config will determine the display order of the fields
          mt.fields = [...configuredFields];
        }

        mt.fields.forEach((mf: MembershipField) => {
          tempFields.push({
            ...mf,
            apiParam: mf.apiParam + mtIdx,
            requiredType: mt.required
          });
        });
      });
      setDisplayFields(tempFields);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipProductLevel]);

  // Set formRefs and formData when displayFields are set
  useEffect(() => {
    if (displayFields && displayFields.length) {
      const apiParams = displayFields.map((item) => item.apiParam);

      const initialFormRefs: Record<string, HTMLInputElement | null> = {};
      formRefs.current = apiParams.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, initialFormRefs);

      const initialFormData: Record<string, string> = {};
      const tempRef = apiParams.reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, initialFormData);

      // Set initial form data and refs
      setError({ ...tempRef });

      // Prefill fields (of the first type) with member info
      const prefilledIndexes: number[] = [];
      if (tempRef) {
        Object.keys(tempRef).forEach((field, idx) => {
          const tempField = field.replace('0', '');
          if (customer && tempField in customer) {
            tempRef[field] = String((customer as any)[tempField] || '');
            prefilledIndexes.push(idx);
          }
        });
        if (tempRef.phone && phoneCountry) {
          const phoneNumber = tempRef.phone.split(phoneCountry.code)[1];
          if (phoneNumber) {
            setPhoneNumberInput({ keyboard: phoneNumber, selectedCountry: phoneCountry });
          }
        }
      }
      // Save the indexes, used to disable those fields
      if (prefilledIndexes && prefilledIndexes.length) {
        setPrefilledFieldIndexes(prefilledIndexes);
      }

      // Set UI loading state based on optional types and prefilled fields
      const optionalTypes = membershipProductTypes.some((mt) => !mt.required);
      if (optionalTypes || !prefilledIndexes.length || prefilledIndexes.length !== displayFields.length) {
        setUILoading(false);
      }

      setFormData(tempRef);
    } else if (membershipProductLevel) {
      // Handle the case of no input fields
      setUILoading(false);
    }
  }, [displayFields]);

  // Validation for prefilled fields
  useEffect(() => {
    if (prefilledFieldIndexes && prefilledFieldIndexes.length) {
      // Determine if all required fields are prefilled
      const areRequiredFieldsPrefilled = displayFields.every(
        (df, idx) => !df.requiredType || !df.required || prefilledFieldIndexes.includes(idx)
      );

      if (areRequiredFieldsPrefilled) {
        // Validate the whole form if all required fields are prefilled
        validateForm();
      } else {
        // Else validate just the prefilled fields
        prefilledFieldIndexes.forEach((i) => validate(i));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledFieldIndexes]);

  // if all fields prefilled: nav forward or setUILoading=false based on the first validation
  // validationComplete never gets reset, so this should only fire after the first validation
  useEffect(() => {
    const optionalTypes = membershipProductTypes.some((mt) => !mt.required);
    if (
      validationComplete &&
      prefilledFieldIndexes &&
      prefilledFieldIndexes.length &&
      prefilledFieldIndexes.length === displayFields.length &&
      !optionalTypes
    ) {
      if (validation) onConfirmation();
      else setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationComplete]);

  // Update focus when the selected input field changes
  useEffect(() => {
    // Check if apiKey is valid, and prevent the effect from running if it's not.
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
      return;
    }

    // Get the actual HTMLInputElement from formRefs.current
    const selectedInput = formRefs.current[apiKey];

    // Only proceed if the selected input element actually exists (is not null)
    if (selectedInput) {
      setTimeout(() => {
        if (inputControl) {
          if (selectedInput.type === 'text') {
            selectedInput.setSelectionRange(selectedInput.value.length, selectedInput.value.length, 'forward');
          }
          selectedInput.focus();
        }
        // Use the container if it is the PhoneNumberInput
        const phoneContainer = selectedInput.closest('[data-phone-number-input-container]');
        const elementToScroll = phoneContainer || selectedInput;
        elementToScroll.scrollIntoView({ block: 'end' });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputIndex, inputControl, apiKey]);

  useEffect(() => {
    if (selectedField) {
      setTimeout(() => {
        handleKeyBoardVisibility(selectedField.keyboard);
      }, 250);
    }
  }, [selectedField]);

  // Re-validate the field if it had an error and was updated
  useEffect(() => {
    if (error[apiKey]) {
      setError({ ...error, [apiKey]: '' });
      const currIndex = displayFields.findIndex((item) => item.apiParam === apiKey);
      validate(currIndex);
    }

    if (locations && selectedField && selectedField.apiParam === 'postalCode') {
      resetLocations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (isReservationPending && !cartLoading && !cartError) {
      // Trigger reservation of membership after items are removed/released
      onConfirmation();
    }
  }, [cartLoading]);

  // Go to the cart page after reserving
  useEffect(() => {
    if (cart && validation && !cartReserving && !cartError) onForwardNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReserving]);

  // Handle initial population of country options
  useEffect(() => {
    setLocationsLoading(false);
    setLocationFieldsDisabled(true);

    if (locations && locations.length === 0) {
      setError({ ...error, postalCode0: t('validation.postalCodeNotFound') });
      setValidation(false);
    } else if (locations && locations.length > 0) {
      // Populate country options
      const uniqueCountries = [...new Set(locations.map((location) => location.country))];
      const sortedCountries = priorityCountries.length
        ? uniqueCountries.sort((countryA, countryB) => {
          const priorityCountryA = priorityCountries.indexOf(countryA);
          const priorityCountryB = priorityCountries.indexOf(countryB);

          if (priorityCountryA === -1) return 1;
          if (priorityCountryB === -1) return -1;

          return priorityCountryA - priorityCountryB;
        })
        : uniqueCountries;
      setCountryOptions(sortedCountries);

      // Set default country if options are available
      if (uniqueCountries.length > 0) {
        setAPIKey('country0');
        const defaultCountry = uniqueCountries[0];
        setFormValueIfKeyExists(defaultCountry, 'country0');
        setSelectedCountryOption(defaultCountry);
      }
    }
  }, [locations]);

  // Update states, or cities if 0 states, based on selected country
  useEffect(() => {
    if (selectedCountryOption && locations) {
      // Filter states based on selected country
      const filteredStates = [
        ...new Set(
          locations.filter((location) => location.country === selectedCountryOption).map((location) => location.state)
        )
      ];
      setAPIKey('state0');
      setStateOptions(filteredStates);
      const defaultState = filteredStates[0] || '';
      setSelectedStateOption(defaultState);

      let defaultCity = '';
      if (!defaultState) {
        // Update cities based on the selected country if there is no state
        const filteredCities = [
          ...new Set(
            locations.filter((location) => location.country === selectedCountryOption).map((location) => location.city)
          )
        ];
        setAPIKey('city0');
        setCityOptions(filteredCities);
        defaultCity = filteredCities[0] || '';
        setSelectedCityOption(defaultCity);
      }

      // update the form values, only if the keys exist on the form
      const updatedFormData: any = {};
      if (Object.keys(formData).includes('state0')) updatedFormData.state0 = defaultState;
      if (Object.keys(formData).includes('city0')) updatedFormData.city0 = defaultCity;
      setFormData({ ...formData, ...updatedFormData });
    }
  }, [selectedCountryOption]);

  // Update cities based on selected state
  useEffect(() => {
    if (selectedStateOption && locations) {
      // Update cities based on the selected state
      const filteredCities = [
        ...new Set(
          locations.filter((location) => location.state === selectedStateOption).map((location) => location.city)
        )
      ];
      setAPIKey('city0');
      setCityOptions(filteredCities);
      const defaultCity = filteredCities[0] || '';
      setFormValueIfKeyExists(defaultCity, 'city0');
      setSelectedCityOption(defaultCity);
    }
  }, [selectedStateOption]);

  useEffect(() => {
    if (selectedCityOption && locations) {
      validateForm();
    }
  }, [selectedCityOption]);

  useEffect(() => {
    if (customerError && locationsLoading) {
      setLocationsLoading(false);
      setLocationFieldsDisabled(false);
      resetCustomerError();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerError]);

  // Construct the payload to reserve the membership
  const getReserveMembershipPayload = () => {
    const typesToReserve: any = [];
    membershipProductTypes.forEach((mt: MembershipType, mtIdx: number) => {
      // Only add the required or selected types
      if (mt.required || selectedTypeIndexes.includes(mtIdx)) {
        const fields: any = {};
        // Check formData for user input
        mt.fields.forEach((f: MembershipField) => {
          const formDataKey = f.apiParam + mtIdx;
          if (formData[formDataKey]) {
            fields[f.apiParam] = formData[formDataKey];
          }
        });
        // Add the reserveParams, and the fields if there are any
        typesToReserve.push({
          reserveParams: mt.reserveParams,
          fields: Object.keys(fields).length > 0 ? fields : undefined
        });
      }
    });

    return { types: typesToReserve };
  };

  // Navigate back
  const onPrevPageClick = () => {
    resetLocations();
    navigate('/ticketing/membership-level-selection', { state: { from: location.pathname } });
  };

  // Navigate forward, or show confirmation overlay
  const onNextPageClick = () => {
    resetLocations();
    const confirmationOverlayEnabled = get(membershipProductSettings, 'dataCollection.confirmationOverlay.enabled');
    // show the confirmation overlay if enabled
    if (confirmationOverlayEnabled) {
      setShowConfirmationOverlay(true);
    } else {
      onConfirmation();
    }
  };

  /**
   * Remove items from the cart if configured to do so.
   * Also set isReservationPending to true if items are being removed,
   * to indicate that reservation should occur after removal is complete.
   *
   * Returns true if items are being removed, false otherwise.
   */
  const removeItemsFromCartIfApplicable = () => {
    // Return false if there is no need to even check the cart
    const productsToRemoveFromCart: string[] = get(
      membershipProductSettings,
      'reservation.productsToRemoveFromCart',
      []
    );
    if (isReservationPending || !cart || !productsToRemoveFromCart.length) {
      return false;
    }

    // Filter cart products to only those that need to be removed, or all products if wildcard is used
    const filteredCartProducts = productsToRemoveFromCart.includes('*')
      ? cart.products
      : cart.products.filter((product) => productsToRemoveFromCart.includes(product.name));

    // Build payload of items to remove
    const removePayload: Item[] = filteredCartProducts.reduce<Item[]>((acc, product) => {
      if (product.items) {
        acc.push(...product.items.map((item) => item.removeParams));
      }
      return acc;
    }, []);

    // Return false if there are no items to remove
    if (removePayload.length === 0) return false;

    // Initiate the removal or release, and return true
    setIsReservationPending(true);
    if (removePayload.length === cart.quantity) {
      releaseCart();
    } else {
      removeTicketsFromCart(removePayload);
    }
    setShowConfirmationOverlay(false);

    return true;
  };

  const onConfirmation = () => {
    // Determine if items need to be removed from the cart, based on config
    // If applicable, this will trigger the removal and return true
    const areItemsBeingRemoved = removeItemsFromCartIfApplicable();

    // If items are being removed, wait for that to complete before reserving
    if (areItemsBeingRemoved) return;

    // Reserve the membership
    const payload = getReserveMembershipPayload();
    reserveMembership(payload);
    setShowConfirmationOverlay(false);
  };

  // Navigate forward to upsell selection
  const onForwardNav = () => {
    if (upsellProducts.length === 1) {
      // if on the only upsell product, set this flag so it does not select the product again
      setUpsell({ ...upsell, singleUpsellProductOffered: true });
    }

    navigate('/ticketing/upsell-selection');
  };

  const onCartErrorConfirm = () => {
    resetCartError();
    setIsReservationPending(false);
  };

  // Get the localized html string for the benefits of the specified level or "allLevels"
  const getConfiguredBenefitText = (key: string) => {
    return get(membershipProductSettings, `benefits.${key}.${i18n.language}`);
  };

  const getLocalizedText = (textObj: LocalizedStrRecord = {}, apiParam?: string): string => {
    return get(textObj, `${i18n.language}`) || t(`inputFieldLabel.${apiParam}`);
  };

  /**
   * Returns the component to use as the label for an input field.
   *
   * There is an error when passing `label` to InputField if it is a child of InputMask.
   * The workaround is to render the label separately, which requires additional
   * styles to match what is applied to the `label` of other InputField(s).
   */
  const getLabelText = (field: MembershipField) => {
    const { required, label, apiParam, inputMask } = field;

    const typographyStyles = inputMask ? { padding: '0 24px', margin: '0 0 10px' } : {};

    return (
      <Typography fontSize={24} fontWeight={600} sx={typographyStyles}>
        {required ? <span style={{ color: 'red' }}>* </span> : ''}
        {getLocalizedText(label, apiParam)}
      </Typography>
    );
  };

  // Get the label for the Next button on the keyboard
  const getNextText = (index: number) => {
    let nextLabel = t('commonText.next');

    const nextDisplayFieldIndex = getNextActiveDisplayFieldIndex(index);
    const nextDisplayField = displayFields[nextDisplayFieldIndex];
    if (!nextDisplayField || !formRefs.current[nextDisplayField.apiParam]) {
      nextLabel = t('commonText.done');
    }

    return nextLabel;
  };

  // Add or remove an optional type from selectedTypeIndexes
  const onTileClick = (typeIdx: number) => {
    const tempTypeIndexes = [...selectedTypeIndexes];

    // determine if the tile is being selected or deselected
    const savedIdx = tempTypeIndexes.indexOf(typeIdx);
    if (savedIdx > -1) {
      // deselect: remove from selected array
      tempTypeIndexes.splice(savedIdx, 1);
    } else {
      // select: add to selected array
      tempTypeIndexes.push(typeIdx);
    }

    handleControlsHide();
    setSelectedTypeIndexes(tempTypeIndexes);
  };

  const debouncedGetLocations = debounce((postalCode: string) => {
    getLocations({ postalCode });
  }, 300); // Adjust the debounce delay as needed

  const handleBlur = (item: MembershipField, typeIdx: number) => {
    setSelectedField(null);
    handleControlsHide();
    // Clear previous data and states
    setCountryOptions([]);
    setStateOptions([]);
    setCityOptions([]);
    setSelectedCountryOption('');
    setSelectedStateOption('');
    setSelectedCityOption('');
    setLocationsLoading(false);

    // reset the form values, only if the keys exist on the form
    const updatedFormData: any = {};
    if (Object.keys(formData).includes('country0')) updatedFormData.country0 = '';
    if (Object.keys(formData).includes('state0')) updatedFormData.state0 = '';
    if (Object.keys(formData).includes('city0')) updatedFormData.city0 = '';
    setFormData({ ...formData, ...updatedFormData });

    // validate the input value and call getLocations
    if (formData[item.apiParam + typeIdx]) {
      const currItem = displayFields[inputIndex];
      const validateInputError = validateInput(currItem, formData);
      const validateInputTypeError = validateInputType(formData[item.apiParam + typeIdx], item.inputType || '');

      if (!validateInputError && !validateInputTypeError) {
        setLocationsLoading(true);
        debouncedGetLocations(formData[item.apiParam + typeIdx]);
      }
    }
  };

  const handleControlsHide = () => {
    setTimeout(() => {
      setHideScrollBar(false);
      setInputControl('');
      validateForm();
    }, 0);
  };

  const handleKeyBoardVisibility = (type = 'alphaNumeric') => {
    if (type === 'numeric' || type === 'date') {
      setInputControl('numpad');
    } else {
      setInputControl('keyboard');
    }
    setHideScrollBar(true);
  };

  const handleFocus = (field: MembershipField, fieldIndex: number, typeIndex: number) => {
    setSelectedField(field);
    setAPIKey(field.apiParam + typeIndex);
    setInputIndex(fieldIndex);
  };

  /**
   * Returns the index of the next active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getNextActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let nextDisplayField;
    do {
      tempIndex++;
      nextDisplayField = displayFields[tempIndex];
    } while (
      tempIndex < displayFields.length &&
      (!formRefs.current[nextDisplayField.apiParam] || formRefs.current[nextDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  /**
   * Returns the index of the previous active display field.
   * It skips over fields that are either not present in formRefs or are disabled.
   */
  const getPreviousActiveDisplayFieldIndex = (index: number) => {
    let tempIndex = index;
    let prevDisplayField;
    do {
      tempIndex--;
      prevDisplayField = displayFields[tempIndex];
    } while (
      tempIndex > -1 &&
      (!formRefs.current[prevDisplayField.apiParam] || formRefs.current[prevDisplayField.apiParam]?.disabled)
    );

    return tempIndex;
  };

  // email domain shortcuts for the keyboard
  const getSuggestions = () => {
    let result;
    if (selectedField && selectedField.keyboard === 'email') {
      result = emailSuggestions;
    }

    return result;
  };

  // Handles the keyboard key click -- changes input accordingly
  const onKeyboardClick = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (selectedField && selectedField.inputType === 'phone') {
      // Logic for phone number input (this part seems to handle its own keyboard state correctly)
      if (v === 'delete') {
        setPhoneNumberInput((prevState) => ({
          ...prevState,
          keyboard: prevState.keyboard.slice(0, prevState.keyboard.length - 1)
        }));
      } else if (v === 'clear') {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: '' }));
      } else {
        setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: prevState.keyboard + v })); // Forward to phone number component
      }
    } else {
      const currentFieldValue = String(formData[apiKey] || '');

      if (v === 'delete') {
        setFormData((prev) => ({
          ...prev,
          [apiKey]: currentFieldValue.slice(0, currentFieldValue.length - 1)
        }));
      } else if (v === 'clear') {
        setFormData((prev) => ({ ...prev, [apiKey]: '' }));
      } else {
        if (selectedField && selectedField.maxChar) {
          if (selectedField.maxChar > currentFieldValue.length) {
            setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
          }
        } else {
          setFormData((prev) => ({ ...prev, [apiKey]: currentFieldValue + v }));
        }
      }
    }
  };

  /**
   * Handles navigation from the keyboard, switching between fields
   * or hiding the keyboard if no more fields are available.
   */
  const onKeyboardNavigationClick = (index: number, direction: string) => {
    // validate the current field
    validate(index);

    // get the new field to focus, whether it is next or back
    let newDisplayFieldIndex;
    if (direction === 'next') {
      newDisplayFieldIndex = getNextActiveDisplayFieldIndex(index);
    } else {
      newDisplayFieldIndex = getPreviousActiveDisplayFieldIndex(index);
    }
    const newDisplayField = displayFields[newDisplayFieldIndex];

    if (newDisplayField && formRefs.current[newDisplayField.apiParam]) {
      // if another displayField: change focus to it
      setAPIKey(newDisplayField.apiParam);
      setSelectedField(newDisplayField);
      setInputIndex(newDisplayFieldIndex);
    } else {
      // else: hide the keyboard
      handleControlsHide();
    }
  };

  const isValidInput = (item: string, type: string, key: string) => {
    if (type === 'phone') {
      const { keyboard, selectedCountry } = phoneNumberInput;
      if (keyboard && selectedCountry && keyboard.length < selectedCountry.size) {
        setError((prevState) => ({
          ...prevState,
          [key]: i18n.t('validation.phoneMinDigitsError', { phoneMinDigits: selectedCountry && selectedCountry.size })
        }));
        setValidation(false);
        return false;
      }
    } else {
      const errorMsg = validateInputType(item, type);
      if (errorMsg) {
        setError((prevState) => ({ ...prevState, [key]: errorMsg }));
        setValidation(false);
        return false;
      }
    }

    return true;
  };

  const validate = (currIndex: number) => {
    const currItem = displayFields[currIndex] as ConfigInputField;
    const errorStr = validateInput(currItem, formData);

    if (errorStr) {
      setError((prevState) => ({ ...prevState, [currItem.apiParam]: errorStr }));
      setValidation(false);
      return false;
    } else if (formData[currItem.apiParam] && currItem.inputType) {
      return isValidInput(formData[currItem.apiParam], currItem.inputType, currItem.apiParam);
    }
    return true;
  };

  const validateForm = () => {
    let validCount = 0;
    let totalCount = 0;

    displayFields.forEach((item, index) => {
      const valid = validate(index);
      if (valid || !formRefs.current[item.apiParam]) validCount++;
      if (validCount === displayFields.length) {
        setValidation(true);
      } else {
        setValidation(false);
      }
      totalCount++;
      if (totalCount === displayFields.length) {
        setValidationComplete(true);
      }
    });
  };

  const setPhoneNumber = (phoneNumber: string, phNumInp: typeof phoneNumberInput, apiParam: string) => {
    setFormData((prev) => ({
      ...prev,
      [apiParam]: phoneNumber
    }));

    setPhoneNumberInput(phNumInp);
    setPhoneCountry(phNumInp.selectedCountry);
  };

  /**
   * Sets the value of formData[apiParam], if apiParam exists on the form.
   *
   * Example use case is to prevent 'country' from being added to the form
   * when doing a postal code lookup, if that field is not configured.
   */
  const setFormValueIfKeyExists = (value: string, apiParam: string) => {
    if (Object.keys(formData).length && Object.keys(formData).includes(apiParam)) {
      setFormData({ ...formData, [apiParam]: value });
    }
  };

  const handleSelectCountryChange = (value: string, item: MembershipField) => {
    setSelectedCountryOption(value);
    setFormValueIfKeyExists(value, `${item.apiParam}0`);
  };
  const handleSelectStateChange = (value: string, item: MembershipField) => {
    setSelectedStateOption(value);
    setFormValueIfKeyExists(value, `${item.apiParam}0`);
  };
  const handleSelectCityChange = (value: string, item: MembershipField) => {
    setSelectedCityOption(value);
    setFormValueIfKeyExists(value, `${item.apiParam}0`);
  };

  const renderStandardTextInput = (field: MembershipField, idx: number, typeIdx: number, isDisabled: boolean) => {
    const disabled = prefilledFieldIndexes.includes(idx) || isDisabled;

    return (
      <InputField
        size="large"
        fullWidth
        type="text"
        name={field.apiParam}
        label={getLabelText(field)}
        placeholder={getLocalizedText(field.placeholder ? field.placeholder : field.label, field.apiParam)}
        value={formData[field.apiParam + typeIdx]}
        error={Boolean(error[field.apiParam + typeIdx])}
        onFocus={(_e) => {
          if (disabled) return;
          handleFocus(field, idx, typeIdx);
        }}
        onClick={(_e) => {
          if (disabled) return;
          setInputIndex(idx);
          handleKeyBoardVisibility(field.keyboard);
        }}
        sx={{ height: '80px' }}
        className={classes.inputField}
        disabled={disabled}
        inputRef={(el: HTMLInputElement | null) => {
          formRefs.current[field.apiParam + typeIdx] = el;
        }}
      />
    );
  };

  const renderCircularProgress = (customStyles: any) => (
    <CircularProgress
      color="inherit"
      size={36}
      style={{
        position: 'absolute',
        color: color.GREEN,
        ...customStyles
      }}
    />
  );

  const renderPostalLookupFields = (
    field: MembershipField,
    fieldIndex: number,
    typeIndex: number,
    options: string[],
    onChange: (value: string, item: MembershipField) => void
  ) => {
    return (
      <>
        <Box sx={{ position: 'relative' }}>
          {options.length > 1 ? (
            <Grid sx={{ marginTop: '4px' }}>
              <SelectDropdown
                gridProps={{ sx: { height: '80px', padding: '0 56px' } }}
                label={getLabelText(field)}
                options={options}
                onChange={onChange}
                item={field}
              />
            </Grid>
          ) : (
            renderStandardTextInput(field, fieldIndex, typeIndex, locationFieldsDisabled)
          )}
          {locationsLoading && renderCircularProgress({ right: '50px', bottom: '22px' })}
        </Box>
      </>
    );
  };

  // Render a single input field; conditionals for phone and inputMask
  const renderFormFields = (membershipInput: MembershipField, idx: number, typeIdx: number) => {
    switch (membershipInput.inputType) {
      case 'phone':
        return (
          <Box fullWidth>
            <PhoneNumberInput
              ref={(el: HTMLInputElement | null) => {
                formRefs.current[membershipInput.apiParam + typeIdx] = el;
              }}
              gridProps={{
                sx: { height: '80px' }
              }}
              textInputProps={{
                name: 'value',
                placeholder: getLocalizedText(
                  membershipInput.placeholder ? membershipInput.placeholder : membershipInput.label,
                  membershipInput.apiParam
                ),
                fullWidth: true
              }}
              label={getLabelText(membershipInput)}
              inputModel={phoneNumberInput}
              setPhoneNumber={(phoneNumber, number) =>
                setPhoneNumber(phoneNumber, number, membershipInput.apiParam + typeIdx)
              }
              onFocus={() => handleFocus(membershipInput, idx, typeIdx)}
              onClick={() => {
                setInputIndex(idx);
                handleKeyBoardVisibility(membershipInput.keyboard);
              }}
              error={error[membershipInput.apiParam + typeIdx] || undefined}
            />
          </Box>
        );
      case 'lookupPostalCode':
        return (
          <>
            <Box sx={{ position: 'relative' }}>
              <InputField
                size="large"
                fullWidth
                type="text"
                name={membershipInput.apiParam}
                label={getLabelText(membershipInput)}
                placeholder={getLocalizedText(
                  membershipInput.placeholder ? membershipInput.placeholder : membershipInput.label,
                  membershipInput.apiParam
                )}
                value={formData[membershipInput.apiParam + typeIdx] || ''}
                error={Boolean(error[membershipInput.apiParam + typeIdx])}
                onFocus={(_e) => handleFocus(membershipInput, idx, typeIdx)}
                onBlur={(_e) => handleBlur(membershipInput, typeIdx)}
                onClick={(_e) => {
                  setInputIndex(idx);
                  handleKeyBoardVisibility(membershipInput.keyboard);
                }}
                sx={{ height: '80px' }}
                className={classes.inputField}
                inputRef={(el: HTMLInputElement | null) => {
                  formRefs.current[membershipInput.apiParam + typeIdx] = el;
                }}
              />
              {locations && locations.length > 0 && (
                <Icon
                  name="CheckCircleOutline"
                  sx={{
                    color: color.GREEN,
                    fontSize: '36px',
                    position: 'absolute',
                    right: '50px',
                    bottom: '22px'
                  }}
                />
              )}
            </Box>
          </>
        );
      case 'autoCountry':
        return renderPostalLookupFields(membershipInput, idx, typeIdx, countryOptions, handleSelectCountryChange);

      case 'autoState':
        return renderPostalLookupFields(membershipInput, idx, typeIdx, stateOptions, handleSelectStateChange);

      case 'autoCity':
        return renderPostalLookupFields(membershipInput, idx, typeIdx, cityOptions, handleSelectCityChange);

      default:
        return (
          <>
            {membershipInput.inputMask ? (
              <>
                {getLabelText(membershipInput)}
                <InputMask
                  mask={membershipInput.inputMask}
                  value={formData[membershipInput.apiParam + typeIdx] || ''}
                  onFocus={(_e) => handleFocus(membershipInput, idx, typeIdx)}
                  name={membershipInput.apiParam}
                  maskPlaceholder={getLocalizedText(
                    membershipInput.placeholder ? membershipInput.placeholder : membershipInput.label,
                    membershipInput.apiParam
                  )}
                  onClick={(_e) => {
                    setInputIndex(idx);
                    handleKeyBoardVisibility(membershipInput.keyboard);
                  }}
                >
                  <InputField
                    size="large"
                    fullWidth
                    type="text"
                    inputProps={{ maxLength: 8 }}
                    placeholder={getLocalizedText(
                      membershipInput.placeholder ? membershipInput.placeholder : membershipInput.label,
                      membershipInput.apiParam
                    )}
                    error={Boolean(error[membershipInput.apiParam + typeIdx])}
                    sx={{ height: '80px' }}
                    className={classes.inputField}
                    inputRef={(el: HTMLInputElement | null) => {
                      formRefs.current[membershipInput.apiParam + typeIdx] = el;
                    }}
                  />
                </InputMask>
              </>
            ) : (
              renderStandardTextInput(membershipInput, idx, typeIdx, false)
            )}
          </>
        );
    }
  };

  const renderFormFieldErrorMessage = (field: MembershipField, typeIdx: number) => {
    if (
      (!field.inputType || field.inputType !== 'phone') &&
      error[field.apiParam + typeIdx] &&
      error[field.apiParam + typeIdx].length
    ) {
      return (
        <Typography color={color.RED} fontSize={textSize.kiosk.SMALL} sx={{ padding: '0 0 0 58px' }}>
          {error[field.apiParam + typeIdx]}
        </Typography>
      );
    }

    return undefined;
  };

  // Error overlay when removing or reserving fails
  if (cartError) {
    const errorMessages = get(membershipProductSettings, 'reservation.errorMessages');

    return (
      <ActionOverlay
        title={
          <Typography variant="h2" align="center">
            {get(errorMessages, `header.${i18n.language}`, t('membershipDataCollection.reserveErrorHeader'))}
          </Typography>
        }
        confirmText={get(errorMessages, `buttons.${i18n.language}[0]`, t('commonText.close'))}
        height={660}
        description={
          <Box width={600}>
            <Typography variant="h4" align="center">
              {get(errorMessages, `subheader.${i18n.language}`, t('saleError.additionalAssistance', { support }))}
            </Typography>
          </Box>
        }
        onConfirm={onCartErrorConfirm}
      />
    );
  }

  // Loading overlay
  if (cartReserving || uiLoading || isReservationPending) {
    let loadingMessage = '';
    if (cartReserving) {
      loadingMessage = get(
        membershipProductSettings,
        `reservation.loadingMessages.header.${i18n.language}`,
        t('membershipDataCollection.reservingHeader')
      );
    }

    return <LoadingOverlay message={loadingMessage} style={{ zIndex: '99' }} />;
  }

  return (
    <>
      <Grid container className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          {/* Title */}
          <MuiBox className={classes.headerContainer}>
            <Typography variant="h2">
              {get(
                membershipProductSettings,
                `dataCollection.messages.header.${i18n.language}`,
                t('membershipLevelSelection.header')
              )}
            </Typography>
            <Typography fontSize={30}>
              {get(
                membershipProductSettings,
                `dataCollection.messages.subheader.${i18n.language}`,
                t('membershipDataCollection.subheader')
              )}
            </Typography>
          </MuiBox>

          {/* Selected level and benefits (left side or top) */}
          <Grid container direction="column" className={classes.selectedLevelContainer}>
            <FlexBox
              border={`3px solid ${primaryPalette}`}
              height="auto"
              sx={{ minHeight: '80px', overflow: 'hidden' }}
            >
              <FlexBox padding="2 4" align="center" justify="between" width="100%">
                <Grid className={classes.membershipLevelName}>
                  <Typography variant="h4">{membershipProductLevel.name}</Typography>
                </Grid>
                <Box width={2} margin="0 3" height={70} style={{ backgroundColor: color.LIGHT_GRAY }} />
                <FlexBox width={100} align="center" justify="end">
                  <Typography fontSize={24} fontWeight={700}>
                    {formatCurrency(membershipProductLevel.basePrice, currencyConfig, languageSettings)}
                  </Typography>
                </FlexBox>
              </FlexBox>
              {getConfiguredBenefitText(membershipProductLevel.name) && (
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(getConfiguredBenefitText(membershipProductLevel.name))
                  }}
                  style={{ width: '100%', minHeight: '80px' }}
                />
              )}
            </FlexBox>
          </Grid>

          {membershipProductTypes && membershipProductTypes.length && displayFields.length ? (
            <>
              {/* Data entry fields (right side or bottom) */}
              <Grid container direction="column" className={classes.dataEntryContainer}>
                <FlexBox sx={{ minHeight: '15.0rem' }} direction="column" noWrap>
                  <Scrollbar hideScrollBar={hideScrollBar}>
                    <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                      {/* Section for each ticket type and it's associated fields within the membership level */}
                      {membershipProductTypes.map((type: MembershipType, typeIdx: number) => {
                        const isTypeSelected = selectedTypeIndexes.includes(typeIdx);

                        return (
                          <>
                            {/* Ticket type tile: only displayed if there is more than 1 type, and it either has fields or is an optional add-on */}
                            {membershipProductTypes.length > 1 && (!type.required || type.fields.length) ? (
                              <FlexBox
                                border={
                                  type.required || isTypeSelected
                                    ? `3px solid ${primaryPalette}`
                                    : `solid 3px ${color.LIGHT_GRAY}`
                                }
                                height="auto"
                                sx={{ minHeight: '92px' }}
                                key={typeIdx}
                              >
                                <FlexBox
                                  padding="0 4"
                                  align="center"
                                  justify="between"
                                  onClick={() => (type.required ? {} : onTileClick(typeIdx))}
                                  sx={{ minHeight: '86px' }}
                                  width="100%"
                                >
                                  <FlexBox flexOverride="1">
                                    {type.required ? (
                                      <Grid className={classes.membershipTypeName}>
                                        <Typography variant="h4">{type.name}</Typography>
                                      </Grid>
                                    ) : (
                                      <Checkbox
                                        size="medium"
                                        label={
                                          <Grid>
                                            <Typography variant="h4">{type.name}</Typography>
                                          </Grid>
                                        }
                                        checked={isTypeSelected ? true : false}
                                      />
                                    )}
                                  </FlexBox>
                                  {/* Price: only displayed if it is an optional add-on */}
                                  {!type.required && (
                                    <>
                                      <Box
                                        width={2}
                                        margin="0 3"
                                        height={70}
                                        sx={{ backgroundColor: color.LIGHT_GRAY }}
                                      />
                                      <FlexBox width={100} align="center" justify="end">
                                        <Typography fontSize={24} fontWeight={700}>
                                          + {formatCurrency(type.price, currencyConfig, languageSettings)}
                                        </Typography>
                                      </FlexBox>
                                    </>
                                  )}
                                </FlexBox>
                              </FlexBox>
                            ) : null}

                            {/* Input fields for the type */}
                            {type.fields.map((field: MembershipField) => {
                              const isFieldDisplayed = type.required || isTypeSelected;
                              displayFieldIdx++;
                              const tempIdx = displayFieldIdx;

                              return (
                                <>
                                  {isFieldDisplayed && (
                                    <Grid key={tempIdx} item xs={12} className={classes.inputFieldContainer}>
                                      {renderFormFields(field, tempIdx, typeIdx)}
                                      {renderFormFieldErrorMessage(field, typeIdx)}
                                    </Grid>
                                  )}
                                </>
                              );
                            })}
                          </>
                        );
                      })}
                    </FlexBox>
                  </Scrollbar>
                </FlexBox>
              </Grid>
            </>
          ) : (
            <>
              {/* No types/fields to show */}
              <FlexBox height="600px" align="center" padding="0 8">
                <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
              </FlexBox>
            </>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        nextText={t('commonText.next')}
        onNextClick={onNextPageClick}
        isNextDisabled={!validation || locationsLoading}
      />

      {/* Keyboard */}
      {inputControl === 'keyboard' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
          <Grid>
            <Keyboard
              language={i18n.language}
              onClick={onKeyboardClick}
              onNext={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onPrev={() => onKeyboardNavigationClick(inputIndex, 'back')}
              nextText={getNextText(inputIndex)}
              prevText={inputIndex ? t('commonText.previous') : t('commonText.back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              suggestions={getSuggestions()}
              onSuggestionClick={onKeyboardClick}
            />
          </Grid>
        </AbsoluteBox>
      )}

      {/* Numpad */}
      {inputControl === 'numpad' && (
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <Grid>
            <NumPadAction
              onClick={onKeyboardClick}
              onConfirm={() => onKeyboardNavigationClick(inputIndex, 'next')}
              onBack={() => onKeyboardNavigationClick(inputIndex, 'back')}
              onCaretClick={(_e) => handleControlsHide()}
              onClickOutside={(_e) => handleControlsHide()}
              layout="row"
              confirmConfig={{
                label: getNextText(inputIndex),
                backLabel: inputIndex ? t('commonText.previous') : t('commonText.back'),
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          </Grid>
        </AbsoluteBox>
      )}

      {/* Confirmation overlay */}
      {showConfirmationOverlay && (
        <ActionOverlay
          title={get(
            membershipProductSettings,
            `dataCollection.confirmationOverlay.messages.header.${i18n.language}`,
            t('membershipDataCollection.confirmationHeader')
          )}
          description={get(
            membershipProductSettings,
            `dataCollection.confirmationOverlay.messages.subheader.${i18n.language}`,
            t('membershipDataCollection.confirmationSubheader')
          )}
          cancelText={get(
            membershipProductSettings,
            `dataCollection.confirmationOverlay.messages.buttons.${i18n.language}[0]`,
            t('membershipDataCollection.confirmationCancelButton')
          )}
          onCancel={() => setShowConfirmationOverlay(false)}
          confirmText={get(
            membershipProductSettings,
            `dataCollection.confirmationOverlay.messages.buttons.${i18n.language}[1]`,
            t('membershipDataCollection.confirmationConfirmButton')
          )}
          onConfirm={() => onConfirmation()}
          height={500}
        />
      )}
    </>
  );
};

export default MembershipDataCollectionView;
